import { gql, useMutation } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import { CANCEL_WORKFLOW } from '@ovComponents/3-pattern/cancelWorkFlowDialog/cancelWorkFlowDialog';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { ActionContext } from '@pages/ViewDetail/actionContext';
import type { ActiveWorkflowCompletion } from '@pages/ViewDetail/viewPage.workflows.query';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { FlagsmithFlags } from '@utils/flagsmith';
import { hydrateWorkflowCompletionContext } from '@utils/hydrateWorkflowCompletionContext';
import { withRetry } from '@utils/retry';
import Queue from 'promise-queue';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useWorkflowCompletionIdentity } from '../../../hooks/useWorkflowCompletionIdentity';
import { useLeavingWithProposal } from './subSteps/modelChangeProposal/useLeavingWithProposal';
import {
  NEXT_STEP,
  PREV_STEP,
  UPDATE_WORKFLOW_COMPLETION,
} from './subSteps/queries';
import { WorkflowCompletionVisual } from './workflowCompletion.visual';
import { WorkflowContext } from './workflowCompletionContext';
import type { WorkflowDataType } from './workflowTypes';
import { reconcileWorkflowAccountContext } from './workflowTypes';

export type { WorkflowContextType } from './workflowCompletionContext';
export type {
  CreatedAccountsType,
  CustodianDepositTransferContext,
  CustodianDepositTransferContextInput,
  CustodianDepositTransferContextResponse,
  TransferContextData,
  WorkflowCustodianDepositTransfer,
  WorkflowDataType,
} from './workflowTypes';

export {
  appendUpdatedAccount,
  buildClearedCreatedFields,
  CREATED_OBJECT_FIELDS,
  reconcileWorkflowAccountContext,
  toTransferContextData,
} from './workflowTypes';

export { WorkflowContext };

export const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
        incompleteFields
        iDCheckCompleted
        iDVerified
        inProvinceSince
        hasCheckedPolicyAndCondition
        affiliateOnly
      }
    }
  }
`;

const workflowCompletionUpdateQueue = new Queue(1);

export const WorkflowCompletion = ({
  workflowCompletion,
  open,
  onClose,
  isInnerWorkflow,
  updateParent,
  onCloseIconClick = () => onClose(),
}: {
  workflowCompletion: ActiveWorkflowCompletion;
  open: boolean;
  onClose: () => void;
  isInnerWorkflow?: boolean;
  updateParent?: (activeCompletion?: any) => void;
  onCloseIconClick?: () => any;
}) => {
  const [workflowData, setWorkflowDataState] = useState<WorkflowDataType>(
    workflowCompletion?.context ?? {},
  );
  const { refreshWidgets } = useContext(ActionContext);
  const { t } = useTranslation('workflowCompletions');
  const setWorkflowData = async (
    action:
      | WorkflowDataType
      | ((prevState: WorkflowDataType) => WorkflowDataType),
  ) => {
    await new Promise<void>((resolve) => {
      setWorkflowDataState((previousData) => {
        const newData =
          typeof action === 'function' ? action(previousData) : action;
        if (
          newData &&
          Object.keys(newData).length > 0 &&
          workflowCompletion?.id
        ) {
          withRetry(
            () =>
              new Promise<void>((innerResolve, innerReject) => {
                updateWorkflowCompletion({
                  variables: {
                    input: {
                      workflowCompletionId: workflowCompletion.id,
                      context: newData,
                    },
                  },
                  onCompleted: () => innerResolve(),
                  onError: (error: Error) => innerReject(error),
                });
              }),
            3, // maxRetries
            500, // delayMs
          ).then(resolve);
        }
        return newData;
      });
    });
  };
  const [activeWorkflowCompletion, setActiveWorkflowCompletion] =
    useState<any>(workflowCompletion);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [stepEnteredAt, setStepEnteredAt] = useState<number>(Date.now());
  // Held as the completion it belongs to rather than as a yes or no, and in a
  // ref rather than in state. The workflow object is handed back afresh on
  // every context save, so a flag cleared whenever one arrives calls a finished
  // workflow abandoned as soon as a straggling save lands behind it — and the
  // last step closes the drawer in the same breath as finishing, before a state
  // change would have been read back.
  const completedWorkflowCompletionId = useRef<string | undefined>(undefined);
  const hasCompletedWorkflow = () =>
    !!workflowCompletion?.id &&
    completedWorkflowCompletionId.current === workflowCompletion.id;

  const getWorkflowType = (workflowCompletionItem: any): string => {
    const rawWorkflowType =
      workflowCompletionItem?.workflow?.type ??
      workflowCompletionItem?.workflow?.workflowType ??
      workflowCompletionItem?.workflow?.key ??
      workflowCompletionItem?.workflow?.name;
    return typeof rawWorkflowType === 'string'
      ? rawWorkflowType.toLowerCase().replaceAll(' ', '_')
      : 'unknown';
  };

  const getStepName = (workflowCompletionItem: any): string =>
    workflowCompletionItem?.currentSubStep?.type ??
    workflowCompletionItem?.currentSubStep?.options?.title?.en ??
    'unknown';

  const checkIfItIsFirstWorkflow = (activeWorkflowCompletionItem: any) => {
    if (Object.keys(activeWorkflowCompletionItem).length === 0) return;
    if (
      activeWorkflowCompletionItem.currentStep.id ===
        activeWorkflowCompletionItem.workflow.steps[0].id &&
      activeWorkflowCompletionItem.currentSubStep.id ===
        activeWorkflowCompletionItem.workflow.steps[0].subSteps[0].id
    ) {
      sendAnalytic(ovAnalyticsEvents.workflowsCompletionStart, {
        workflowStepTitle:
          activeWorkflowCompletionItem.currentSubStep.options?.title,
        stepName: getStepName(activeWorkflowCompletionItem),
        workflowType: getWorkflowType(activeWorkflowCompletionItem),
        workflowId: activeWorkflowCompletionItem?.workflow?.id,
        workflowName: activeWorkflowCompletionItem?.workflow?.name,
        activeWorkflowCompletionId: activeWorkflowCompletionItem?.id,
        objectId: activeWorkflowCompletionItem?.objectId,
        objectType: activeWorkflowCompletionItem?.objectType,
      });
    }
  };
  const trackWorkflowStepAbandonment = (exitMethod: 'close' | 'back') => {
    if (!hasCompletedWorkflow() && activeWorkflowCompletion?.id) {
      sendAnalytic('workflow_step_abandoned', {
        activeWorkflowCompletionId: activeWorkflowCompletion.id,
        workflowId: activeWorkflowCompletion?.workflow?.id,
        workflowName: activeWorkflowCompletion?.workflow?.name,
        workflowType: getWorkflowType(activeWorkflowCompletion),
        stepName: getStepName(activeWorkflowCompletion),
        timeOnStep: Date.now() - stepEnteredAt,
        exitMethod,
      });
    }
  };
  const handleClose = () => {
    trackWorkflowStepAbandonment('close');
    onClose?.();
  };

  /**
   * Closes the run itself, and says whether it went.
   *
   * A discarded proposal leaves the workflow with nothing left to do: resuming
   * it would open on steps built around a change that is no longer there, and
   * until it is closed the household goes on offering to carry on with it.
   */
  const cancelThisWorkflow = async (): Promise<boolean> => {
    if (!workflowCompletion?.id) return false;

    try {
      await cancelWorkflowCompletion({
        variables: {
          input: {
            transition: 'cancel',
            workflowCompletionId: workflowCompletion.id,
          },
        },
      });
      await updateParent?.();
      !isInnerWorkflow && refreshWidgets?.();
      return true;
    } catch (caught) {
      datadogLogs.logger.error(
        'Cancelling a workflow whose proposal was discarded failed',
        { workflowCompletionId: workflowCompletion.id },
        caught instanceof Error ? caught : undefined,
      );
      return false;
    }
  };

  const leaving = useLeavingWithProposal({
    // A finished workflow closes itself the moment its last step is passed,
    // and the proposal it leaves out for signature is the point of having run
    // it. Only a workflow walked out of has left one behind.
    proposalLeftBehind: () =>
      hasCompletedWorkflow() ? undefined : workflowData.modelChangeProposalId,
    onDiscarded: async () => {
      if (await cancelThisWorkflow()) return;

      // Still there to be resumed, so it must not be left naming a proposal
      // that has been called back.
      await setWorkflowData((previousData) => ({
        ...previousData,
        modelChangeProposalId: undefined,
      }));
    },
  });

  const { resolvesOwner, ownerId } =
    useWorkflowCompletionIdentity(workflowCompletion);
  const completionUserId = resolvesOwner
    ? ownerId
    : workflowCompletion.objectId;
  // Flags fail closed until resolved, so an unresolved flag reads as off and
  // keeps the original hydration. The effect re-runs once the flag resolves on.
  const contextAware = useFeatureFlag(FlagsmithFlags.context_aware_workflows);
  const [updateIdCheckWithComplianceReview] = useMutation(UPDATE_USER, {
    variables: {
      input: {
        userId: completionUserId,
        iDCheckCompleted: true,
        complianceState: 'NEEDS_REVIEW',
        complianceIssueSource: 'User opted out of ID verification',
      },
    },
    onCompleted: (): void => {},
  });

  const [nextStepMutation, { loading }] = useMutation(NEXT_STEP());
  const nextStep: typeof nextStepMutation = async (
    ...args: Parameters<typeof nextStepMutation>
  ) => {
    const previousStepName = getStepName(activeWorkflowCompletion);
    try {
      const result = await nextStepMutation(...args);
      const { data } = result;
      // Action steps are always skipped (we don't return them). If we do, it means that it is the last substep.
      const isActionStep =
        data.nextSubStep.workflowCompletion.currentSubStep.type.includes(
          'ACTION',
        );
      const isLastSubStep =
        data.nextSubStep.workflowCompletion.currentSubStep.id ===
        activeWorkflowCompletion.currentSubStep.id;

      const reconciledContext = reconcileWorkflowAccountContext(
        data.nextSubStep.workflowCompletion.context,
      );
      const reconciledWorkflowCompletion = {
        ...data.nextSubStep.workflowCompletion,
        context: reconciledContext,
      };

      setWorkflowDataState(reconciledContext);
      if (updateParent) {
        await updateParent(reconciledWorkflowCompletion);
      }

      if (isLastSubStep || isActionStep) {
        completedWorkflowCompletionId.current =
          data.nextSubStep.workflowCompletion?.id;
        sendAnalytic(ovAnalyticsEvents.workflowsCompletionEnd, {
          workflowStepTitle:
            data.nextSubStep.workflowCompletion.currentSubStep.options?.title,
          stepName: getStepName(data.nextSubStep.workflowCompletion),
          workflowType: getWorkflowType(data.nextSubStep.workflowCompletion),
          workflowId: data.nextSubStep.workflowCompletion?.workflow?.id,
          workflowName: data.nextSubStep.workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: data.nextSubStep.workflowCompletion?.id,
          objectId: data.nextSubStep.workflowCompletion?.objectId,
          objectType: data.nextSubStep.workflowCompletion?.objectType,
        });
        handleClose();
        !isInnerWorkflow && refreshWidgets?.();
        if (updateParent) {
          await updateParent();
        }
      }

      setActiveWorkflowCompletion({
        ...reconciledWorkflowCompletion,
        direction: 'FORWARD',
      });
      setStepEnteredAt(Date.now());

      return result;
    } catch (error: any) {
      sendAnalytic('workflow_error_displayed', {
        errorType: error?.message ?? 'unknown',
        stepName: previousStepName,
        workflowType: getWorkflowType(activeWorkflowCompletion),
        activeWorkflowCompletionId: activeWorkflowCompletion?.id,
      });
      throw error;
    }
  };

  const [previousStep, { loading: prevLoading }] = useMutation(PREV_STEP(), {
    variables: {
      input: { workflowCompletionId: workflowCompletion.id },
    },
    onCompleted: (data) => {
      setActiveWorkflowCompletion({
        ...data.previousSubStep.workflowCompletion,
        direction: 'BACKWARD',
      });
    },
  });

  const [cancelWorkflowCompletion] = useMutation(CANCEL_WORKFLOW);

  const [updateWorkflowCompletionMutation] = useMutation(
    UPDATE_WORKFLOW_COMPLETION(),
    {
      variables: {
        input: {
          workflowCompletionId: workflowCompletion.id,
          context: workflowData,
        },
      },
    },
  );
  const updateWorkflowCompletion = (
    ...args: Parameters<typeof updateWorkflowCompletionMutation>
  ) =>
    workflowCompletionUpdateQueue.add(() =>
      updateWorkflowCompletionMutation(...args),
    );

  useEffect(() => {
    setActiveWorkflowCompletion(workflowCompletion);
    setStepEnteredAt(Date.now());
    checkIfItIsFirstWorkflow(workflowCompletion);
  }, [workflowCompletion]);

  useEffect(() => {
    if (workflowCompletion.context) {
      setWorkflowDataState(
        hydrateWorkflowCompletionContext({
          context: workflowCompletion.context,
          objectType: workflowCompletion.objectType,
          objectId: workflowCompletion.objectId,
          contextAware,
        }),
      );
    }
  }, [workflowCompletion, contextAware]);

  const goNext = async (fromKey?: string) => {
    if (fromKey && typeof fromKey === 'string') {
      await nextStep({
        variables: {
          input: { workflowCompletionId: workflowCompletion.id, fromKey },
        },
      });
    } else {
      await nextStep({
        variables: { input: { workflowCompletionId: workflowCompletion.id } },
      });
    }
  };

  return (
    <WorkflowContext.Provider
      value={{
        workflowData,
        setWorkflowData,
        updateWorkflowContext: updateWorkflowCompletion,
        userId: ownerId ?? undefined,
      }}
    >
      <WorkflowCompletionVisual
        onCloseIconClick={() =>
          leaving.leave(() => {
            trackWorkflowStepAbandonment('close');
            onCloseIconClick();
          })
        }
        workflowCompletion={activeWorkflowCompletion}
        open={open}
        onClose={() => leaving.leave(handleClose)}
        onNext={goNext}
        previousStep={async () => {
          trackWorkflowStepAbandonment('back');
          await previousStep();
          setStepEnteredAt(Date.now());
        }}
        loading={loading || prevLoading}
        setActiveWorkflowCompletion={setActiveWorkflowCompletion}
        isInnerWorkflow={isInnerWorkflow}
        handleIdSkip={() => updateIdCheckWithComplianceReview()}
      />
      <ConfirmationModal
        open={leaving.asking}
        loading={leaving.discarding}
        testIdPrefix='discard-model-change-proposal'
        title={t('discardModelChangeProposal.title')}
        bodyText={t('discardModelChangeProposal.body')}
        confirmButtonLabel={t('discardModelChangeProposal.discard')}
        cancelButtonLabel={t('discardModelChangeProposal.keep')}
        confirmButtonColor='error'
        onConfirm={() => {
          void leaving.discard();
        }}
        onCancel={leaving.keep}
      />
    </WorkflowContext.Provider>
  );
};
